import { toast } from "sonner";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { useCreateDocument } from "../../hooks/useCreateDocument";
import { useClipboard } from "../../hooks/useClipboard";
import { convertHtmlToSfdt } from "../../services/document.service";

export interface UseMessageActionsOptions {
  documentId?: string;
  documentTitle?: string;
}

const insertMessageIntoWord = async (messageText: string) => {
  const htmlContent = await marked(messageText);
  const sanitizedHtml = DOMPurify.sanitize(htmlContent);
  await Word.run(async (context) => {
    const selection = context.document.getSelection();
    selection.insertHtml(sanitizedHtml, Word.InsertLocation.replace);
    await context.sync();
  });
};

const saveToBankInWord = async (messageText: string) => {
  await Word.run(async (context) => {
    const properties = context.document.properties.customProperties;
    properties.load("items");
    await context.sync();
    const timestamp = new Date().getTime();
    const key = `bank_item_${timestamp}`;
    properties.add(key, messageText);
    await context.sync();
  });
};

export const useMessageActions = (options: UseMessageActionsOptions = {}) => {
  const { mutateAsync: createDoc, isPending: isCreatingDocument } = useCreateDocument();
  const { copyHtmlToClipboard } = useClipboard();

  const handleCopyMessage = async (messageText: string) => {
    const htmlContent = await marked(messageText);
    const sanitizedHtml = DOMPurify.sanitize(htmlContent);
    await copyHtmlToClipboard(sanitizedHtml, messageText, "Message copied to clipboard");
  };

  const handleInsertMessageToDocument = async (messageText: string) => {
    try {
      await insertMessageIntoWord(messageText);
      toast.success("Inserted!", { description: "Message inserted into document" });
    } catch (err) {
      console.error("Failed to insert message:", err);
      toast.error("Insert Failed", { description: "Failed to insert message into document" });
    }
  };

  const handleCreateDocumentFromMessage = async (messageText: string, customTitle?: string) => {
    if (options.documentId == null || options.documentTitle == null) {
      toast.error("Error", { description: "Document information not available" });
      return;
    }
    try {
      const htmlContent = await marked(messageText);
      const sanitizedHtml = DOMPurify.sanitize(htmlContent);
      const sfdtContent = await convertHtmlToSfdt(sanitizedHtml);
      const editorId =
        Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      const title = customTitle ?? `${options.documentTitle}-AI-Discussion`;
      const result = await createDoc({
        editor_id: editorId,
        title: title,
        content: sfdtContent,
        collaborators: [],
        folderId: null,
        doc_type: "aila",
      });
      toast.success("Success!", {
        description: `Document created successfully. Document ID: ${result.document_id}`,
      });
      if (result.document_id) {
        window.open(`${process.env.APP_FRONTEND_URL}/d/${result.document_id}`, "_blank");
      }
    } catch (error) {
      console.error("Failed to create document:", error);
      toast.error("Creation Failed", {
        description: "Failed to create document. Please try again.",
      });
    }
  };

  const handleSaveToBank = async (messageText: string) => {
    try {
      await saveToBankInWord(messageText);
      toast.success("Saved to Bank!", {
        description: "Message saved successfully and can be reused later",
      });
    } catch (err) {
      console.error("Failed to save to bank:", err);
      toast.error("Save Failed", { description: "Failed to save message to bank" });
    }
  };

  return {
    handleCopyMessage,
    handleInsertMessageToDocument,
    handleCreateDocumentFromMessage,
    handleSaveToBank,
    isCreatingDocument,
  };
};
